/**
 * PublicSeriesDetailPage
 *
 * Read-only page for viewing a public series.
 * Accessible to anyone without authentication.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BookOpen,
  Users,
  Globe,
  Palette,
  User,
  Heart,
  Eye,
  Sparkles,
  Clock,
  CheckCircle,
  PauseCircle,
  Star
} from 'lucide-react';
import { getBookTypeLabel } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Status configuration
const statusConfig = {
  active: { icon: Clock, color: '#22C55E', bg: '#DCFCE7', label: 'Active' },
  completed: { icon: CheckCircle, color: '#3B82F6', bg: '#DBEAFE', label: 'Completed' },
  hiatus: { icon: PauseCircle, color: '#F59E0B', bg: '#FEF3C7', label: 'On Hiatus' }
};

// Role colors
const roleColors = {
  protagonist: { bg: '#DCFCE7', color: '#22C55E' },
  supporting: { bg: '#DBEAFE', color: '#3B82F6' },
  antagonist: { bg: '#FEE2E2', color: '#EF4444' },
  mentor: { bg: '#F3E8FF', color: '#9333EA' },
  sidekick: { bg: '#FEF3C7', color: '#F59E0B' }
};

const PublicSeriesDetailPage = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();

  const [series, setSeries] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  useEffect(() => {
    loadSeriesData();
  }, [seriesId]);

  const loadSeriesData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/v1/series/public/${seriesId}`, {
        withCredentials: true
      });
      const seriesData = response.data.series || response.data;
      setSeries(seriesData);
      setBooks(response.data.books || []);
      setIsFavorited(seriesData.is_favorited || false);
      setFavoritesCount(seriesData.favorites_count || 0);

      // Track view (deduplicated on backend)
      axios.post(`${API}/v1/series/public/${seriesId}/view`, {}, {
        withCredentials: true
      }).catch(() => {}); // Fire and forget, ignore errors
    } catch (err) {
      console.error('Failed to load series:', err);
      if (err.response?.status === 404) {
        setError('Series not found or is not public.');
      } else {
        setError('Failed to load series. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteClick = async () => {
    if (isTogglingFavorite) return;

    setIsTogglingFavorite(true);
    try {
      const response = await axios.post(
        `${API}/v1/series/${seriesId}/favorite`,
        {},
        { withCredentials: true }
      );

      setIsFavorited(response.data.favorited);
      setFavoritesCount(response.data.favorites_count);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please log in to favorite series');
      } else {
        toast.error('Failed to update favorite');
      }
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: '#4F46E5' }} />
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading series...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !series) {
    return (
      <PageLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
            <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
              Oops!
            </p>
            <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
              {error || 'Series not found.'}
            </p>
            <button
              onClick={() => navigate('/explore')}
              className="px-5 py-2.5 rounded-lg font-semibold text-sm"
              style={{
                background: 'linear-gradient(135deg, #818CF8 0%, #4F46E5 100%)',
                color: 'white',
                fontFamily: 'Quicksand, sans-serif'
              }}
            >
              Browse Series
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const status = statusConfig[series.status] || statusConfig.active;
  const StatusIcon = status.icon;

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-4 md:py-6 md:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 mb-4 text-sm font-semibold transition-colors hover:opacity-70"
            style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Series Header */}
          <div className="rounded-2xl overflow-hidden shadow-sm mb-6" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
            {/* Cover Image */}
            <div
              className="relative h-48 md:h-64"
              style={{ background: 'linear-gradient(145deg, #E0E7FF 0%, #C7D2FE 100%)' }}
            >
              {series.cover_image_url ? (
                <img
                  src={getImageUrl(series.cover_image_url)}
                  alt={series.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <BookOpen className="w-16 h-16" style={{ color: '#818CF8' }} />
                </div>
              )}

              {/* Status Badge */}
              <div
                className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1"
                style={{ background: status.bg, color: status.color }}
              >
                <StatusIcon className="w-3.5 h-3.5" />
                {status.label}
              </div>
            </div>

            {/* Series Info */}
            <div className="p-4 md:p-6">
              <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                {series.title}
              </h1>

              {series.description && (
                <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  {series.description}
                </p>
              )}

              {/* Author Row */}
              <div className="flex items-center gap-3 mb-4 pb-4" style={{ borderBottom: '1px solid var(--st-border-light)' }}>
                {series.author_picture ? (
                  <img
                    src={getImageUrl(series.author_picture)}
                    alt={series.author_name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--st-border-light)' }}
                  >
                    <User className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                  </div>
                )}
                <div>
                  <p className="font-bold text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
                    {series.author_name || 'Anonymous'}
                  </p>
                  <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    Created {formatDate(series.created_at)}
                  </p>
                </div>
              </div>

              {/* Stats Row */}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                  <BookOpen className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                  <span className="font-semibold">{series.book_count || 0}</span>
                  <span className="text-xs" style={{ color: 'var(--st-text-muted)' }}>books</span>
                </div>

                {series.characters?.length > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                    <Users className="w-4 h-4" style={{ color: 'var(--st-primary)' }} />
                    <span className="font-semibold">{series.characters.length}</span>
                    <span className="text-xs" style={{ color: 'var(--st-text-muted)' }}>characters</span>
                  </div>
                )}

                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
                  <Eye className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                  <span className="font-semibold">{series.views_count || 0}</span>
                </div>

                <button
                  onClick={handleFavoriteClick}
                  disabled={isTogglingFavorite}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all hover:scale-105 disabled:opacity-50"
                  style={{
                    background: isFavorited ? '#FEE2E2' : 'var(--st-bg-muted)',
                    color: 'var(--st-text-heading)'
                  }}
                  title={isFavorited ? 'Unfavorite' : 'Favorite this series'}
                >
                  <Heart
                    className="w-4 h-4 transition-colors"
                    style={{
                      color: isFavorited ? '#EF4444' : 'var(--st-primary)',
                      fill: isFavorited ? '#EF4444' : 'none'
                    }}
                  />
                  <span className="font-semibold">{favoritesCount}</span>
                </button>

                {/* Series Settings Pills */}
                <div className="flex items-center gap-2 ml-auto flex-wrap">
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)' }}>
                    {getBookTypeLabel(series.age_range)}
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold capitalize" style={{ background: '#E0E7FF', color: '#4F46E5' }}>
                    {series.art_style?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Characters Section */}
          {series.characters?.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Characters
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {series.characters.map((character) => {
                  const roleStyle = roleColors[character.role] || roleColors.supporting;
                  return (
                    <div
                      key={character.id}
                      className="rounded-xl p-3 flex items-start gap-3"
                      style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
                    >
                      {/* Portrait */}
                      <div
                        className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden"
                        style={{ background: 'linear-gradient(145deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
                      >
                        {character.portrait_url ? (
                          <img
                            src={getImageUrl(character.portrait_url)}
                            alt={character.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-6 h-6" style={{ color: '#FFCDB2' }} />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-sm truncate" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                            {character.name}
                          </h3>
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize flex-shrink-0"
                            style={{ background: roleStyle.bg, color: roleStyle.color }}
                          >
                            {character.role}
                          </span>
                        </div>
                        <p className="text-xs line-clamp-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          {character.description}
                        </p>
                        {character.traits?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {character.traits.slice(0, 3).map((trait, i) => (
                              <span
                                key={i}
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                                style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-muted)' }}
                              >
                                {trait}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Books Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5" style={{ color: 'var(--st-primary)' }} />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                Books in this Series
              </h2>
            </div>

            {books.length === 0 ? (
              <div className="rounded-2xl p-8 text-center" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                <BookOpen className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--st-border-medium)' }} />
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  No public books in this series yet.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                {books.map((book, index) => {
                  const storyVersion = book.version || book.story_version;
                  return (
                  <div
                    key={book.id}
                    onClick={() => navigate(`/story/${book.id}`)}
                    className="flex items-center gap-4 p-3 cursor-pointer transition-colors hover:opacity-90"
                    style={{ borderBottom: index < books.length - 1 ? '1px solid var(--st-border-light)' : 'none' }}
                  >
                    {/* Book Number */}
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-sm"
                      style={{ background: '#E0E7FF', color: '#4F46E5', fontFamily: 'Chewy, cursive' }}
                    >
                      {index + 1}
                    </div>

                    {/* Thumbnail */}
                    <div
                      className="w-14 h-14 rounded-lg flex-shrink-0 overflow-hidden"
                      style={{ background: 'linear-gradient(145deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
                    >
                      {book.thumbnail ? (
                        <img
                          src={getImageUrl(book.thumbnail)}
                          alt={book.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Sparkles className="w-6 h-6" style={{ color: '#FFCDB2' }} />
                        </div>
                      )}
                    </div>

                    {/* Book Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm truncate" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                          {book.title}
                        </h3>
                        {storyVersion && (
                          <span
                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold flex-shrink-0"
                            style={{
                              background: storyVersion === 3 ? '#F3E8FF' : storyVersion === 2 ? '#EFF6FF' : '#F5F5F5',
                              color: storyVersion === 3 ? '#7C3AED' : storyVersion === 2 ? '#3B82F6' : '#6B7280'
                            }}
                          >
                            {storyVersion === 3 && <Star className="w-2.5 h-2.5" />}
                            v{storyVersion}
                          </span>
                        )}
                      </div>
                      <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                        {book.page_count || book.pages?.length || 0} pages
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="hidden sm:flex items-center gap-3 text-xs flex-shrink-0" style={{ color: 'var(--st-text-muted)' }}>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" />
                        {book.views_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3.5 h-3.5" />
                        {book.favorites_count || 0}
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PublicSeriesDetailPage;
